<template>
    <cr-confirmation-dialog
        :key="currentAgreement?.id"
        :modelValue="showDialog"
        form-instruction
        persistent
        :title="currentAgreement?.name ? currentAgreement.name : t('AGREEMENT_DIALOG_TITLE')"
        @confirm="onConfirm"
        :confirm-disabled="!acceptedCurrentAgreement"
        :confirm-text="t('AGREEMENT_DIALOG_CONFIRM_TEXT')"
        @cancel="cancel"
        :disabled="isLoading"
        class="accept-agreements-dialog"
        data-testing="accept-agreements-dialog"
    >
        <cr-html :value="currentAgreement?.content" :allowed-tags="expectedHtmlTags" />
        <cr-checkbox required :disabled="isLoading" class="cr-mt4" v-model="acceptedCurrentAgreement" :label="t('ACCEPT_AGREEMENTS_DISCLAIMER_TEXT')" name="acknowledgement" data-testing="accept-checkbox"/>
    </cr-confirmation-dialog>
</template>

<style>
.accept-agreements-dialog .cr-confirmation-dialog-form-instruction,
.accept-agreements-dialog .cr-checkbox label,
.accept-agreements-dialog .cr-checkbox .cr-required-indicator
{
    --cr-input-required-indicator-color: white;
    color: white;
}
.accept-agreements-dialog a
{
    color: var(--cr-splash-accent) !important;
}
</style>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IAgreement } from '@cyber-range/cyber-range-api-agreement-client';
import { useAgreementStore } from '../../stores/agreementStore';
import { useApiClientStore } from '../../stores/apiClientStore';
import { storeToRefs } from 'pinia';

const { t } = useI18n();

const emit = defineEmits<{
    (e: 'confirm', state: boolean): void;
    (e: 'cancel', state: boolean): void;
    (e: 'update:modelValue', state: boolean): void;
}>();

const agreementStore = useAgreementStore();
const apiClientStore = useApiClientStore();

const isLoading = computed(() => apiClientStore.isLoading);
const { pendingAgreements } = storeToRefs(agreementStore);

const currentAgreement = computed<IAgreement | undefined>(() => pendingAgreements.value[0]);
const showDialog = computed(() => !!currentAgreement.value?.content);

const acceptedCurrentAgreement = ref(false);
const expectedHtmlTags = [ 'b', 'em', 'u', 'i', 'li', 'ul', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'div' ];

watch(currentAgreement, async (agreement) =>
{
    if (agreement && !agreement.content)
    {
        await agreementStore.fetchAgreementContent(agreement.agreementId);
    }

    acceptedCurrentAgreement.value = false;
}, { immediate: true });

const onConfirm = async () =>
{
    await agreementStore.acceptCurrentAgreement();

    if (!agreementStore.hasPendingAgreements)
    {
        emit('confirm', true);
        close();
    }
};

const close = () =>
{
    emit('update:modelValue', false);
};

const cancel = () =>
{
    emit('cancel', true);
    close();
};
</script>
